import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { PredefinedMilisecondsTypeEnum } from '../../../lib/enums';
import lang from '../../../lib/language';
import { ChangeIndividualUserNameRequest } from '../../../lib/models/crm/types';
import { RootState } from '../../../lib/store';
import { autoLogin as autoLoginCall, getIndividualExtendedInfo } from '../../../lib/store/common-actions';
import { changeIndividualUserName, getIndividualUserName, updateIndividualTransitionToForActive } from '../../../lib/store/crm';
import { CRMCall } from '../../../lib/store/crm/types';
import { isCallStatusReady } from '../../../lib/util/error-handling/StatusByCallHelpers';

import { Preloader } from '../../../components';
import { useIsMobile } from '../../../hooks';
import EnrollmentState from '../../../store-util/EnrollmentState';
import { UI_ROUTES } from '../../../util/routes';
import Layout from '../component/Layout';

import styles from './EnrollSetupUserName.module.scss';

type Props = ReduxProps;

const EnrollSetupUserName = ({
  activateUser,
  autoLogin,
  accessToken,
  isAppLoaded,
  isUserNameUsed,
  tradingPlatformId,
  changeUserName,
  stateData,
  isPhoneConfirmed,
  isEmailConfirmed,
  individualUserName,
  individualId,
  getUserName,
  isChangeUserNameCallReady,
}: Props): JSX.Element => {
  const { form } = EnrollmentState.use();
  const history = useHistory();
  const isMobile = useIsMobile();
  const usernameMinChars = 4;
  const usernameMaxChars = 16;

  const [profile_name, setProfileName] = useState('');
  const [usernameErrorText, setusernameErrorText] = useState('');
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // if enroll was cancelled and resumed, set the appropriate steps
    if (isEmailConfirmed) {
      EnrollmentState.setVerifyEmailStepPassed(true);
    }
    if (isPhoneConfirmed) {
      EnrollmentState.setVerifyPhoneStepPassed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateData, isPhoneConfirmed, isEmailConfirmed]);

  useEffect(() => {
    if (individualId) {
      activateUser();
      getUserName();
    }
  }, [individualId, activateUser, getUserName]);

  useEffect(() => {
    individualUserName && setProfileName(individualUserName);
  }, [individualUserName]);

  useEffect(() => {
    if (!isUserNameUsed && isChangeUserNameCallReady) {
      EnrollmentState.setSetupUserNameStepPassed(true);
      if (stateData.auth.enroll.confirmedSteps?.email) {
        autoLogin();
      }
      history.replace(UI_ROUTES.baseURL);
    }
    if (isUserNameUsed) {
      setShowError(true);
      setusernameErrorText(lang.userNameExist());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserNameUsed, isChangeUserNameCallReady, stateData]);

  useEffect(() => {
    if (isAppLoaded && (!form.steps.isVerifyPhonePassed)) {
      history.replace(UI_ROUTES.enrollPhoneVerificaion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, isAppLoaded, form.steps.isVerifyPhonePassed, tradingPlatformId]);

  const validateUsername = () => {
    if (profile_name.length < usernameMinChars) {
      setusernameErrorText(lang.validationCapitalMinimumSymbols(usernameMinChars));
      setShowError(true);
    } else if (profile_name.length > usernameMaxChars) {
      setusernameErrorText(lang.validationCapitalMaximumSymbols(usernameMaxChars));
      setShowError(true);
    }
  };

  const handleSubmit = () => {
    validateUsername();
    if (!showError) {
      changeUserName({ individual_id: individualId as number, profile_name });
    }
  };

  if (isLoading) {
    // This delay is for the give chance for the BE to create trading accounts
    return (
      <div className={styles.loader}>
        <Preloader
          isFullScreen
          text={lang.commonMessageLoading()}
          owner="EnrollSetupUserName"
          timeOutSetup={{
            timeout: PredefinedMilisecondsTypeEnum.twoSeconds,
            checkIfTimeout: () => true,
            onTimeout: () => {
              setIsLoading(false);
            },
          }}
        />
      </div>
    );
  }

  return (
    <Layout
      formTitle=""
      contentStyle={styles.verificationContent}
      displayBackButton={!isMobile}
      displayFooter={false}
      displayNextButton
      nextButtonText={lang.commonContinueButton()}
      displayLoginButton={false}
      onSubmit={handleSubmit}
    >
      <div className={styles.wrapper}>
        <div className={styles.title}>{`${lang.userNameTitle()}.`}</div>
        <div className={styles.description}>{lang.userNameDescription()}</div>
        <div className={styles.inputWrapper}>
          <div className={styles.inputTitle}>{lang.userNameTitle()}</div>
          <input
            type="text"
            className={classNames(styles.input, showError && styles.inputFail)}
            placeholder={lang.userNameTitle()}
            value={profile_name}
            onChange={(e) => {
              setProfileName(e.target.value);
              setShowError(false);
              setusernameErrorText('');
            }}
          />
          {showError && <div className={styles.inputError}>{usernameErrorText}</div>}
        </div>
      </div>
    </Layout>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  activateUser(): void {
    dispatch(updateIndividualTransitionToForActive());
  },
  changeUserName: (data: ChangeIndividualUserNameRequest) => {
    dispatch(changeIndividualUserName(data));
  },
  getUserName: (): void => dispatch(getIndividualUserName()),
  getExtendedInfo: (): void => {
    dispatch(getIndividualExtendedInfo());
  },
  autoLogin: (): void => {
    dispatch(autoLoginCall());
  },
});

const mapStateToProps = (state: RootState) => ({
  isAppLoaded: state.app.loaded,
  accessToken: state.auth.enroll.accessToken,
  tradingPlatformId: state.crm.tradingPlatformId,
  isChangeUserNameCallReady: isCallStatusReady(CRMCall.changeIndividualUserName, state.crm.statusByCall),
  isUserNameUsed: state.crm.statusByCall[CRMCall.changeIndividualUserName]?.errorCode === 409,
  individualId: state.crm.individualId,
  stateData: state,
  individualUserName: state.crm.individualUserName,
  isPhoneConfirmed: state.crm.individualExtendedInfo?.phone_number_confirmed,
  isEmailConfirmed: state.crm.individualExtendedInfo?.email_confirmed,
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(EnrollSetupUserName);
